import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orderCompletedSchema } from 'modules/sales/settings/order-status/schemas/order-complete.schema';
import { SUB_ORDERS_ONE_KEY } from 'modules/sales/sub-orders/constants/sub-order.queries';
import { SubOrderService } from 'modules/sales/sub-orders/services';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ActionTakenEnum } from '../constants/action-taken.enum';
import { PAID_ORDERS_ONE_KEY } from 'modules/sales/paid-order/constants';
import { useOrderContext } from '../contexts/OrderContext';

const initValues: any = {
  actionTaken: ActionTakenEnum.NO_ACTION,
};

const useSuborderComplete = (orderId: string, defaultValues = initValues, onClose?: () => void) => {
  const { t } = useTranslation('subOrder');
  const { order } = useOrderContext();
  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm({
    resolver: yupResolver(orderCompletedSchema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) resetForm(defaultValues);
  }, [defaultValues, resetForm]);

  const {
    mutate,
    reset: resetMutation,
    error,
    isPending: isLoading,
    isSuccess,
    data,
  } = useMutation((payload) => SubOrderService.complete(orderId, payload), {
    onSuccess: (data, values) => {
      toast.success(t('successCompleted'));
      queryClient.invalidateQueries({ queryKey: [orderId, SUB_ORDERS_ONE_KEY] });
      order?._id && queryClient.invalidateQueries({ queryKey: [order?._id, PAID_ORDERS_ONE_KEY] });
      onClose?.();
    },
  });

  const reset = useCallback(() => {
    resetForm();
    resetMutation();
  }, [resetForm, resetMutation]);

  return {
    control,
    error,
    isLoading,
    isSuccess,
    data,
    reset,
    onSubmit: handleSubmit((values) => {
      mutate(values);
    }),
  };
};

export default useSuborderComplete;
